import type { ReactNode } from 'react'
import { fbt } from 'fbtee'
import invariant from 'tiny-invariant'
import { Check, Clock, Pencil, Ticket } from 'lucide-react'
import { openSlots } from '@shared/eventTickets'
import { Button } from '../../../components/ui/button'
import { Skeleton } from '../../../components/ui/skeleton'
import { UserAvatar } from '../../../components/UserAvatar'
import { friendFallbackName } from '../../../lib/commonStrings'
import { cn } from '../../../lib/utils'
import { OffAppAvatar } from './TicketMarks'
import type { EventTickets, TicketSlot, UserProfile } from '../../../types'

const CONTAINER = 'rounded-lg border border-border bg-card p-3 text-sm'

/** One ticket ledger as its owner, a holder of one of its tickets, or the owner's crew sees it. */
export function TicketLedgerCard({ loading, ledger, view, viewerId, ended, profiles, busy, onEdit, onClaim, onGiveBack }: {
  loading?: boolean
  /** The holder view's viewer — whose slot Give it back returns. */
  viewerId?: string
  /** The event is over: the holder view reads in the past tense. */
  ended?: boolean
  ledger?: EventTickets
  view?: 'owner' | 'holder' | 'crew'
  profiles?: Record<string, UserProfile>
  busy?: boolean
  onEdit?: () => void
  onClaim?: (slotId: string) => void
  onGiveBack?: (slotId: string) => void
}) {
  if (loading) {
    return (
      <div className={cn(CONTAINER, 'space-y-3')}>
        <Skeleton className="h-4 w-32" />
        <Skeleton className="h-3.5 w-48" />
        <Skeleton className="h-3.5 w-40" />
      </div>
    )
  }

  invariant(ledger, 'TicketLedgerCard: ledger is required when not loading')
  invariant(view, 'TicketLedgerCard: view is required when not loading')
  const owner = profiles?.[ledger.userId]
  const ownerName = owner?.displayName?.trim() || friendFallbackName()

  if (view === 'owner') {
    return (
      <div className={CONTAINER} data-testid="ticket-ledger-mine">
        <div className="flex items-center gap-2">
          <Ticket className="size-4 shrink-0 text-primary" />
          <span className="flex-1 font-semibold">
            {fbt(
              'Your tickets (' + fbt.param('count', ledger.tickets.length) + ')',
              'Heading of the card listing the tickets the viewer bought for an event, with how many',
            )}
          </span>
          {onEdit && (
            <Button variant="ghost" size="icon-sm" onClick={onEdit} aria-label={fbt('Edit your tickets', 'Accessible label of the button that edits the viewer’s tickets')}>
              <Pencil />
            </Button>
          )}
        </div>
        <ul className="mt-2 space-y-1.5">
          {ledger.tickets.map(slot => <SlotLine key={slot.id} slot={slot} ownerId={ledger.userId} profiles={profiles} />)}
        </ul>
      </div>
    )
  }

  const header = (title: string) => (
    <div className="flex min-w-0 items-center gap-2.5">
      <UserAvatar profile={owner} fallbackName={ownerName} size="sm" />
      <span className="min-w-0 flex-1 truncate font-medium">{title}</span>
    </div>
  )

  if (view === 'holder') {
    const mine = ledger.tickets.find(s => s.kind === 'user' && s.accepted && s.userId === viewerId)
    return (
      <div className={cn(CONTAINER, 'flex items-center gap-2')} data-testid="ticket-ledger-held">
        <div className="min-w-0 flex-1">
          {header(ended
            ? fbt(fbt.param('name', ownerName) + ' had your ticket', 'Card title after the event: the person who bought the viewer’s ticket')
            : fbt(fbt.param('name', ownerName) + ' has your ticket', 'Card title when someone else bought the viewer’s ticket and the viewer accepted it'))}
        </div>
        {onGiveBack && mine && (
          <Button variant="ghost" size="sm" disabled={busy} onClick={() => onGiveBack(mine.id)}>
            {fbt('Give it back', 'Button returning a ticket someone gave the viewer, so it is open again')}
          </Button>
        )}
      </div>
    )
  }

  const open = openSlots(ledger)
  return (
    <div className={cn(CONTAINER, 'flex items-center gap-2')} data-testid="ticket-ledger-crew">
      <div className="min-w-0 flex-1">
        {header(fbt(
          fbt.param('name', ownerName) + ' has ' +
            fbt.plural('an open ticket', open.length, { showCount: 'ifMany', many: 'open tickets' }),
          'Card title when someone in the viewer’s crew has tickets nobody claimed yet',
        ))}
      </div>
      {onClaim && open[0] && (
        <Button size="sm" disabled={busy} onClick={() => onClaim(open[0].id)}>
          {fbt('I want it', 'Button claiming an open ticket someone in the viewer’s crew bought')}
        </Button>
      )}
    </div>
  )
}

function SlotLine({ slot, ownerId, profiles }: { slot: TicketSlot; ownerId: string; profiles?: Record<string, UserProfile> }) {
  if (slot.kind === 'me') {
    const self = profiles?.[ownerId]
    return <Line icon={<UserAvatar profile={self} fallbackName={self?.displayName} size="xs" />}>{fbt('You', 'A ticket the viewer bought for themselves')}</Line>
  }
  if (slot.kind === 'open') {
    return (
      <Line icon={<span className="size-3.5 rounded-full border border-dashed border-muted-foreground" />} muted>
        {fbt('Open', 'A ticket nobody has claimed yet')}
      </Line>
    )
  }
  if (slot.kind === 'name') return <Line icon={<OffAppAvatar />}>{slot.name}</Line>
  const profile = slot.userId ? profiles?.[slot.userId] : undefined
  return (
    <Line icon={<UserAvatar profile={profile} fallbackName={profile?.displayName} size="xs" />}>
      <span className="truncate">{profile?.displayName?.trim() || friendFallbackName()}</span>
      {slot.accepted ? (
        <Check className="size-3.5 shrink-0 text-going" aria-label={fbt('Accepted', 'Accessible label: the person accepted the ticket')} />
      ) : (
        <span className="flex shrink-0 items-center gap-1 text-xs text-muted-foreground">
          <Clock className="size-3" />
          {fbt('Pending', 'The person a ticket is for hasn’t answered yet')}
        </span>
      )}
    </Line>
  )
}

function Line({ icon, muted, children }: { icon: ReactNode; muted?: boolean; children: ReactNode }) {
  return (
    <li className={cn('flex min-w-0 items-center gap-2', muted && 'text-muted-foreground')}>
      <span className="flex size-5 shrink-0 items-center justify-center">{icon}</span>
      {children}
    </li>
  )
}
